import { useState } from 'react';
import { supabase } from '../supabaseClient';

export default function AuthDebug() {
  const [logs, setLogs] = useState<string[]>([]);
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testPassword, setTestPassword] = useState('TestPassword123!');

  const log = (message: string) => {
    console.log(message);
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testConnection = async () => {
    setLogs([]);
    log('🔍 Testing Supabase Connection...');

    try {
      // Test 1: Check client initialization
      log(`✓ Supabase URL: ${supabase.supabaseUrl}`);

      // Test 2: Check auth endpoint
      const { data: settingsData, error: settingsError } = await supabase.auth.getSession();
      if (settingsError) {
        log(`❌ Auth endpoint error: ${settingsError.message}`);
      } else {
        log(`✓ Auth endpoint accessible`);
        log(`  Session: ${settingsData.session ? 'Active' : 'None'}`);
      }

      // Test 3: Check database connectivity
      const { data: dbTest, error: dbError } = await supabase
        .from('profiles')
        .select('count')
        .limit(1);

      if (dbError) {
        log(`❌ Database error: ${dbError.message}`);
      } else {
        log(`✓ Database accessible`);
      }

      // Test 4: Check auth settings
      try {
        const response = await fetch(`${supabase.supabaseUrl}/auth/v1/settings`, {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        });

        if (response.ok) {
          const settings = await response.json();
          log(`✓ Auth settings retrieved`);
          log(`  Email confirmation: ${settings.external?.email ? 'Required' : 'Disabled'}`);
          log(`  Providers: ${Object.keys(settings.external || {}).join(', ')}`);
        } else {
          log(`⚠️ Auth settings unavailable (${response.status})`);
        }
      } catch (err) {
        log(`⚠️ Could not fetch auth settings`);
      }

      log('');
      log('✅ Connection test complete');
    } catch (err) {
      log(`❌ Fatal error: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const testSignup = async () => {
    log('');
    log('🔍 Testing Sign Up...');

    try {
      const { data, error } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          data: {
            full_name: 'Test User',
          },
        },
      });

      if (error) {
        log(`❌ Sign up error: ${error.message}`);
        log(`   Error code: ${(error as any).code}`);
        log(`   Error status: ${(error as any).status}`);
      } else {
        log(`✓ Sign up request successful`);
        log(`  User ID: ${data.user?.id || 'None'}`);
        log(`  Session: ${data.session ? 'Created' : 'None (email confirmation may be required)'}`);
        log(`  Email: ${data.user?.email || 'None'}`);
        log(`  Confirmed: ${data.user?.confirmed_at ? 'Yes' : 'No'}`);
      }
    } catch (err) {
      log(`❌ Exception: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const testLogin = async () => {
    log('');
    log('🔍 Testing Sign In...');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: testEmail,
        password: testPassword,
      });

      if (error) {
        log(`❌ Sign in error: ${error.message}`);
        log(`   Error code: ${(error as any).code}`);
        log(`   Error status: ${(error as any).status}`);
      } else {
        log(`✓ Sign in successful`);
        log(`  User ID: ${data.user?.id}`);
        log(`  Email: ${data.user?.email}`);
        log(`  Session: ${data.session ? 'Active' : 'None'}`);
      }
    } catch (err) {
      log(`❌ Exception: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const checkExistingUsers = async () => {
    log('');
    log('🔍 Checking for existing users...');

    try {
      // Try to get current session
      const { data: sessionData } = await supabase.auth.getSession();
      log(`Current session: ${sessionData.session ? 'Active' : 'None'}`);

      // Try to count profiles
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (error) {
        log(`❌ Error checking profiles: ${error.message}`);
      } else {
        log(`✓ Profiles in database: ${count || 0}`);
      }
    } catch (err) {
      log(`❌ Exception: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">🔧 Auth Diagnostic Tool</h1>

        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <h2 className="text-xl font-bold mb-4">Test Credentials</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={testPassword}
                onChange={(e) => setTestPassword(e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <h2 className="text-xl font-bold mb-4">Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={testConnection}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-3 rounded-lg font-semibold"
            >
              Test Connection
            </button>
            <button
              onClick={checkExistingUsers}
              className="bg-purple-600 hover:bg-purple-700 px-4 py-3 rounded-lg font-semibold"
            >
              Check Users
            </button>
            <button
              onClick={testSignup}
              className="bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg font-semibold"
            >
              Test Sign Up
            </button>
            <button
              onClick={testLogin}
              className="bg-yellow-600 hover:bg-yellow-700 px-4 py-3 rounded-lg font-semibold"
            >
              Test Sign In
            </button>
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Test Results</h2>
            <button
              onClick={() => setLogs([])}
              className="text-sm text-slate-400 hover:text-white"
            >
              Clear
            </button>
          </div>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm space-y-1 max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <div className="text-slate-500">Click a button above to run tests...</div>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="text-slate-300">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-blue-400 hover:text-blue-300"
          >
            ← Back to App
          </a>
        </div>
      </div>
    </div>
  );
}
